/** Support → dashboard (D-129): volume, first-answer and resolution times, deadlines kept,
 * satisfaction, by channel, category and support member. Figures are counted by the API. */

import { useSupportTicketsDashboardRetrieve } from "@agencyos/api-client";
import { GlassCard, SegmentedControl, Skeleton, StatCard } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Stats {
  key?: string;
  name?: string;
  tickets: number;
  open: number;
  complaints: number;
  first_response_hours: number | null;
  resolution_hours: number | null;
  first_response_sla: number | null;
  resolution_sla: number | null;
  csat_average: number | null;
  csat_answers: number;
  csat_satisfied: number | null;
  reopened: number;
}

interface Board {
  days: number;
  totals: Stats;
  by_channel: Stats[];
  by_category: Stats[];
  by_agent: Stats[];
}

function useFormat() {
  const { t } = useTranslation();
  return {
    hours: (v: number | null) => (v === null ? t("support.dashboard.none") : t("support.dashboard.hours", { value: v })),
    percent: (v: number | null) => (v === null ? t("support.dashboard.none") : t("support.dashboard.percent", { value: v })),
    score: (v: number | null) => (v === null ? t("support.dashboard.none") : t("support.dashboard.score", { value: v })),
  };
}

function Breakdown({ title, rows, label }: { title: string; rows: Stats[]; label: (row: Stats) => string }) {
  const { t } = useTranslation();
  const f = useFormat();
  if (!rows.length) return null;
  return (
    <GlassCard as="section" className="stack" aria-label={title}>
      <h3>{title}</h3>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t("support.dashboard.key")}</th>
              <th scope="col">{t("support.dashboard.tickets")}</th>
              <th scope="col">{t("support.dashboard.firstResponse")}</th>
              <th scope="col">{t("support.dashboard.resolution")}</th>
              <th scope="col">{t("support.dashboard.firstSla")}</th>
              <th scope="col">{t("support.dashboard.csat")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{label(row)}</td>
                <td>{row.tickets}</td>
                <td>{f.hours(row.first_response_hours)}</td>
                <td>{f.hours(row.resolution_hours)}</td>
                <td>{f.percent(row.first_response_sla)}</td>
                <td>{f.score(row.csat_average)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}

export function DashboardTab() {
  const { t } = useTranslation();
  const f = useFormat();
  const [days, setDays] = useState<"7" | "30" | "90">("30");
  const query = useSupportTicketsDashboardRetrieve({ days: Number(days) });
  const board = query.data?.status === 200 ? (query.data.data as unknown as Board) : undefined;
  return (
    <div className="stack">
      <SegmentedControl
        label={t("support.dashboard.period")}
        value={days}
        onChange={setDays}
        options={(["7", "30", "90"] as const).map((value) => ({ value, label: t("support.dashboard.days", { count: Number(value) }) }))}
      />
      {!board ? (
        <Skeleton style={{ height: 200 }} />
      ) : (
        <>
          <section className="kpis" aria-label={t("support.tabs.dashboard")}>
            <StatCard label={t("support.dashboard.tickets")} value={String(board.totals.tickets)} />
            <StatCard label={t("support.dashboard.open")} value={String(board.totals.open)} />
            <StatCard label={t("support.dashboard.complaints")} value={String(board.totals.complaints)} />
            <StatCard label={t("support.dashboard.firstResponse")} value={f.hours(board.totals.first_response_hours)} />
            <StatCard label={t("support.dashboard.resolution")} value={f.hours(board.totals.resolution_hours)} />
            <StatCard label={t("support.dashboard.firstSla")} value={f.percent(board.totals.first_response_sla)} />
            <StatCard label={t("support.dashboard.resolutionSla")} value={f.percent(board.totals.resolution_sla)} />
            <StatCard label={t("support.dashboard.csat")} value={f.score(board.totals.csat_average)} />
            <StatCard label={t("support.dashboard.csatSatisfied")} value={f.percent(board.totals.csat_satisfied)} />
            <StatCard label={t("support.dashboard.reopened")} value={String(board.totals.reopened)} />
          </section>
          <Breakdown title={t("support.dashboard.byChannel")} rows={board.by_channel} label={(row) => t(`support.channel.${row.key ?? ""}`)} />
          <Breakdown title={t("support.dashboard.byCategory")} rows={board.by_category} label={(row) => t(`support.category.${row.key ?? ""}`)} />
          <Breakdown title={t("support.dashboard.byAgent")} rows={board.by_agent} label={(row) => row.name ?? ""} />
        </>
      )}
    </div>
  );
}
